import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import { risottoscriviPush } from '../../hooks/usePushNotifications';
import { getAssociateData } from '../../services/otpAuthService';
import { idPersonaChat } from '../../utils/sezioniDipendente';
import {
  aggiornaBadgeApp,
  chiudiNotificheChat,
  deveSuonareRicevuto,
  idConversazioneValido,
  suonaRicevuto,
} from '../../utils/chatNotificheSito';
import type { EvMessaggio } from '../../../../shared/types/chat';

interface EvBadge { totale?: number; notifiche?: number; chat?: number }

/** Il mio id persona (piano 2): l'id dello studio per il titolare, quello del dipendente per il dipendente. */
const mioId = (): string | null => {
  try {
    return idPersonaChat(getAssociateData()) || null;
  } catch {
    return null;
  }
};

const finestraAFuoco = (): boolean => typeof document !== 'undefined' && typeof document.hasFocus === 'function' && document.hasFocus();
const schedaVisibile = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * Notifiche della chat nella pagina (notifiche fase A, 20/10/2026, spec §10 e §17), dentro il
 * `ChatProvider` dell'area riservata (`ChatAreaRiservata`). Nessuna notifica locale: la web push
 * parte sempre e la mostra il service worker (`public/sw.js`). Qui:
 * - suono leggero `chat-ricevuto` per un messaggio altrui con la finestra in primo piano (≤ 1 ogni 2 s);
 * - notifiche della chat chiuse su `conversazione_letta` e quando la chat si apre (scheda visibile);
 * - numero sull'icona dell'app installata da `badge`/`conversazione_letta`;
 * - messaggi del service worker: `apri-chat` ⇒ naviga alla chat, `risottoscrivi` ⇒ rinnovo dell'iscrizione.
 */
const NotificheChatSito: React.FC = () => {
  const { socket, attiva, conversazioni } = useChat();
  const navigate = useNavigate();
  const conversazioniRef = useRef(conversazioni);
  conversazioniRef.current = conversazioni;
  const attivaRef = useRef(attiva);
  attivaRef.current = attiva;
  const ultimoSuono = useRef(0);

  useEffect(() => {
    const via = [
      socket.on('messaggio', (e: EvMessaggio) => {
        if (!e || !e.conversazioneId || !e.messaggio) return;
        const ora = Date.now();
        const conversazione = conversazioniRef.current.find((c) => c.id === e.conversazioneId) || null;
        const caso = { messaggio: e.messaggio, conversazione, mioId: mioId(), finestraAFuoco: finestraAFuoco(), ora, ultimoSuono: ultimoSuono.current };
        if (!deveSuonareRicevuto(caso)) return;
        ultimoSuono.current = ora;
        suonaRicevuto();
      }),
      socket.on('conversazione_letta', (e: { conversazioneId?: string; badge?: EvBadge }) => {
        if (!e) return;
        if (e.conversazioneId) chiudiNotificheChat(e.conversazioneId);
        if (e.badge && typeof e.badge.totale === 'number') aggiornaBadgeApp(e.badge.totale);
      }),
      socket.on('badge', (b: EvBadge) => {
        if (b && typeof b.totale === 'number') aggiornaBadgeApp(b.totale);
      }),
    ];
    return () => via.forEach((togli) => togli());
  }, [socket]);

  // Chat aperta (o tornata visibile): le sue notifiche non servono più
  useEffect(() => {
    if (attiva && schedaVisibile()) chiudiNotificheChat(attiva);
  }, [attiva]);
  useEffect(() => {
    const cambio = () => {
      if (attivaRef.current && schedaVisibile()) chiudiNotificheChat(attivaRef.current);
    };
    document.addEventListener('visibilitychange', cambio);
    return () => document.removeEventListener('visibilitychange', cambio);
  }, []);

  useEffect(() => {
    const sw = typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : undefined;
    if (!sw || typeof sw.addEventListener !== 'function') return undefined;
    const messaggio = (ev: MessageEvent) => {
      const d = ev && ev.data;
      if (!d || typeof d !== 'object') return;
      if (d.tipo === 'apri-chat' && idConversazioneValido(d.conversazioneId)) {
        navigate(`/area-riservata/chat/${d.conversazioneId}`);
      } else if (d.tipo === 'risottoscrivi') {
        risottoscriviPush().catch(() => undefined);
      }
    };
    sw.addEventListener('message', messaggio);
    return () => sw.removeEventListener('message', messaggio);
  }, [navigate]);

  return null;
};

export default NotificheChatSito;
